// orchestrator_fullstack/frontend/src/app/admin/oidc/UsersRolesPage.tsx
// Users & Roles Page (tenant-scoped)

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import DangerConfirmModal from "../../components/DangerConfirmModal";
import {
  addEmailRole,
  deleteGroupRoleMapping,
  listAudit,
  listEmailRoles,
  listGroupRoleMappings,
  listGroupTenantMappings,
  removeEmailRole,
  upsertGroupRoleMapping,
  validateToken,
  type Role,
  // Model A helpers
  allowedMappingRoles,
  filterPlatformAdminRows,
  canManagePlatformAdmin,
  toTenantUuid,
} from "./api";

function isEmail(s: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
}

/** Normalize unknown thrown values into a readable message */
function errMsg(e: any): string {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e?.message && typeof e.message === "string") return e.message;
  if (e?.detail && typeof e.detail === "string") return e.detail;

  // common API error shape: { detail: { ... } } or { detail: [...] }
  const d = e?.detail;
  try {
    if (typeof d === "string") return d;
    if (d && typeof d === "object") return JSON.stringify(d);
  } catch {
    // ignore
  }

  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

type ConfirmState =
  | null
  | {
      kind: "email" | "group";
      label: string;
      onYes: () => Promise<void>;
    };

export default function UsersRolesPage() {
  const { state } = useAuth();

  // NOTE: state.tenant is a UUID in your /v1/auth/me output.
  const tenantIdRaw = state.tenant || "";
  const tenantUuid = toTenantUuid(tenantIdRaw);
  const rolesStr = (state.roles || []).join(", ");

  // platform-admin controls support mode + can manage platform-admin mappings
  const isPlatformAdmin = canManagePlatformAdmin(state.roles);

  // Support mode (platform-admin only)
  const [supportMode, setSupportMode] = useState(false);
  const [supportReason, setSupportReason] = useState("");

  const supportHeaders = useMemo(() => {
    if (!supportMode) return {};
    const reason = String(supportReason || "").trim();
    return {
      "X-Support-Mode": "1",
      ...(reason ? { "X-Support-Reason": reason } : {}),
    };
  }, [supportMode, supportReason]);

  // UI state
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [emailRows, setEmailRows] = useState<any[]>([]);
  const [groupRoleRows, setGroupRoleRows] = useState<any[]>([]);
  const [groupTenantRows, setGroupTenantRows] = useState<any[]>([]);
  const [audit, setAudit] = useState<{ configured: boolean; items: any[] } | null>(null);

  // Add forms
  const [email, setEmail] = useState("");
  const [emailRole, setEmailRole] = useState<Role>("viewer");

  const [groupName, setGroupName] = useState("");
  const [groupRole, setGroupRole] = useState<Role>("viewer");

  // Token validation
  const [token, setToken] = useState("");
  const [tokenResult, setTokenResult] = useState<any | null>(null);

  // Remove modal
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  const blockingNoTenant = !tenantUuid;

  const supportModeAllowed = isPlatformAdmin;
  const supportModeValid = !supportMode || String(supportReason || "").trim().length >= 5;

  // Model A: dropdown roles depend on current user roles
  const roleOptions: Role[] = useMemo(() => allowedMappingRoles(state.roles), [state.roles]);

  async function refreshAll() {
    setErr(null);
    if (!tenantUuid) return;
    try {
      const [e, g, gt, a] = await Promise.all([
        listEmailRoles(tenantUuid, supportHeaders),
        listGroupRoleMappings(tenantUuid, supportHeaders),
        listGroupTenantMappings(supportHeaders),
        listAudit(tenantUuid, supportHeaders),
      ]);

      // Model A: tenant-admin must not see platform-admin rows
      setEmailRows(filterPlatformAdminRows(e || [], state.roles));
      setGroupRoleRows(filterPlatformAdminRows(g || [], state.roles));
      setGroupTenantRows(gt || []);
      setAudit(a || null);
    } catch (e: any) {
      setErr(errMsg(e));
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantUuid, supportMode]);

  const filteredEmail = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = emailRows || [];
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.email || "").toLowerCase().includes(s) ||
        String(r.role || "").toLowerCase().includes(s)
    );
  }, [emailRows, q]);

  const filteredGroups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = groupRoleRows || [];
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.group_name || "").toLowerCase().includes(s) ||
        String(r.role || "").toLowerCase().includes(s)
    );
  }, [groupRoleRows, q]);

  const groupsMappedToThisTenant = useMemo(() => {
    // group_tenant rows are tenant UUIDs; tenantUuid is UUID
    return (groupTenantRows || []).filter((r) => String(r.tenant_id) === tenantUuid);
  }, [groupTenantRows, tenantUuid]);

  // ------------------------------------------------------------------
  // Frontend guardrail: prevent "orphaning tenant" by removing last admin
  // Applies only to tenant-admins (not platform-admins), because platform-admin can recover.
  // Count admin mappings across BOTH email and group mappings visible to this user.
  // ------------------------------------------------------------------
  const adminMappingsCount = useMemo(() => {
    const e = (emailRows || []).filter((r) => String(r.role) === "admin").length;
    const g = (groupRoleRows || []).filter((r) => String(r.role) === "admin").length;
    return e + g;
  }, [emailRows, groupRoleRows]);

  function removingLastAdminWouldOrphan(targetRole: string): boolean {
    // only tenant-admin needs protection here; platform-admin can recover
    if (isPlatformAdmin) return false;
    if (String(targetRole) !== "admin") return false;
    return adminMappingsCount <= 1;
  }

  if (blockingNoTenant) {
    return (
      <div className="p-6">
        <div className="text-xl font-bold">Users & Roles</div>
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Tenant is not resolved from <code>/v1/auth/me</code>. This page requires a valid tenant context.
        </div>
      </div>
    );
  }

  // Model A: tenant-admin cannot add/remove platform-admin
  const canMutatePlatformAdmin = isPlatformAdmin;

  const emailRoleIsAllowed = canMutatePlatformAdmin || emailRole !== "platform-admin";
  const groupRoleIsAllowed = canMutatePlatformAdmin || groupRole !== "platform-admin";

  return (
    <div className="p-6 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-2xl font-extrabold">Users & Roles</div>
          <div className="text-sm text-zinc-600 mt-1">
            Tenant: <span className="font-mono">{tenantUuid}</span> · You:{" "}
            <span className="font-mono">{state.user || "(unknown)"}</span> · Roles:{" "}
            <span className="font-mono">{rolesStr || "(none)"}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            className="h-10 w-[260px] rounded-xl border border-zinc-200 px-3 text-sm"
            placeholder="Search email or group…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {/* Support mode */}
      {supportModeAllowed && (
        <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold">Support Mode</div>
              <div className="text-xs text-zinc-600">
                Cross-tenant changes must be explicit and auditable. Requires a reason.
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={supportMode}
                onChange={(e) => setSupportMode(e.target.checked)}
              />
              Enabled
            </label>
          </div>

          {supportMode && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-zinc-700">Reason</div>
              <input
                className="mt-1 h-10 w-full rounded-xl border border-zinc-200 px-3 text-sm"
                value={supportReason}
                onChange={(e) => setSupportReason(e.target.value)}
                placeholder="e.g. Customer support case #12345 (min 5 chars)"
              />
              {!supportModeValid && (
                <div className="mt-2 text-xs text-red-600">
                  Reason is required when Support Mode is enabled.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {err}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4">
        {/* Email mappings */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold">Email → Role</div>
            <div className="text-xs text-zinc-500">{filteredEmail.length} mappings</div>
          </div>

          <div className="mt-3 flex flex-col md:flex-row gap-2">
            <input
              className="h-10 flex-1 rounded-xl border border-zinc-200 px-3 text-sm"
              placeholder="email@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select
              className="h-10 rounded-xl border border-zinc-200 px-3 text-sm"
              value={emailRole}
              onChange={(e) => setEmailRole(e.target.value as Role)}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              className="h-10 rounded-xl bg-black px-4 text-sm font-bold text-white disabled:opacity-50"
              disabled={busy || !isEmail(email) || !supportModeValid || !emailRoleIsAllowed}
              title={
                !emailRoleIsAllowed
                  ? "Only platform-admin can assign platform-admin."
                  : undefined
              }
              onClick={async () => {
                setBusy(true);
                setErr(null);
                try {
                  await addEmailRole(tenantUuid, email.trim(), emailRole, supportHeaders);
                  setEmail("");
                  await refreshAll();
                } catch (e: any) {
                  setErr(errMsg(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Add
            </button>
          </div>

          {!isPlatformAdmin && (
            <div className="mt-2 text-xs text-zinc-500">
              Note: platform-admin mappings are hidden on this page for tenant admins.
            </div>
          )}

          {!isPlatformAdmin && adminMappingsCount <= 1 && (
            <div className="mt-2 text-xs text-amber-700">
              Safety: this tenant currently has {adminMappingsCount} admin mapping(s) visible to you.
              You can’t remove the last admin.
            </div>
          )}

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500">
                  <th className="py-2">Email</th>
                  <th className="py-2">Role</th>
                  {isPlatformAdmin && <th className="py-2">Tenant</th>}
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredEmail.map((r) => {
                  const role = String(r.role || "");
                  const lastAdminBlock = removingLastAdminWouldOrphan(role);
                  const platformAdminBlock = !canMutatePlatformAdmin && role === "platform-admin";
                  const disabled = lastAdminBlock || platformAdminBlock;

                  let title: string | undefined;
                  if (platformAdminBlock) title = "Only platform-admin can remove platform-admin mappings.";
                  else if (lastAdminBlock) title = "Cannot remove the last remaining admin mapping for this tenant.";

                  return (
                    <tr key={`${r.email}-${r.role}`} className="border-t border-zinc-100">
                      <td className="py-2 font-mono">{r.email}</td>
                      <td className="py-2">{r.role}</td>
                      {isPlatformAdmin && <td className="py-2 font-mono text-xs">{r.tenant_id}</td>}
                      <td className="py-2 text-right">
                        <button
                          className="rounded-lg border border-zinc-200 px-3 py-1 text-xs font-semibold hover:bg-zinc-50 disabled:opacity-50"
                          disabled={disabled}
                          title={title}
                          onClick={() =>
                            setConfirm({
                              kind: "email",
                              label: `${r.email} → ${r.role}`,
                              onYes: async () => {
                                await removeEmailRole(tenantUuid, r.email, r.role, supportHeaders);
                                await refreshAll();
                              },
                            })
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredEmail.length === 0 && (
                  <tr>
                    <td colSpan={isPlatformAdmin ? 4 : 3} className="py-4 text-xs text-zinc-500">
                      No email mappings.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Group role mappings */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold">Group → Role</div>
            <div className="text-xs text-zinc-500">{filteredGroups.length} mappings</div>
          </div>

          <div className="mt-3 flex flex-col md:flex-row gap-2">
            <input
              className="h-10 flex-1 rounded-xl border border-zinc-200 px-3 text-sm"
              placeholder="Group name (exact match from token claim)"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <select
              className="h-10 rounded-xl border border-zinc-200 px-3 text-sm"
              value={groupRole}
              onChange={(e) => setGroupRole(e.target.value as Role)}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <button
              className="h-10 rounded-xl bg-black px-4 text-sm font-bold text-white disabled:opacity-50"
              disabled={busy || !groupName.trim() || !supportModeValid || !groupRoleIsAllowed}
              title={
                !groupRoleIsAllowed
                  ? "Only platform-admin can assign platform-admin."
                  : undefined
              }
              onClick={async () => {
                setBusy(true);
                setErr(null);
                try {
                  await upsertGroupRoleMapping(tenantUuid, groupName.trim(), groupRole, supportHeaders);
                  setGroupName("");
                  await refreshAll();
                } catch (e: any) {
                  setErr(errMsg(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Add
            </button>
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500">
                  <th className="py-2">Group</th>
                  <th className="py-2">Role</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((r) => {
                  const role = String(r.role || "");
                  const lastAdminBlock = removingLastAdminWouldOrphan(role);
                  const platformAdminBlock = !canMutatePlatformAdmin && role === "platform-admin";
                  const disabled = lastAdminBlock || platformAdminBlock;

                  let title: string | undefined;
                  if (platformAdminBlock) title = "Only platform-admin can remove platform-admin mappings.";
                  else if (lastAdminBlock) title = "Cannot remove the last remaining admin mapping for this tenant.";

                  return (
                    <tr key={`${r.group_name}-${r.role}`} className="border-t border-zinc-100">
                      <td className="py-2 font-mono">{r.group_name}</td>
                      <td className="py-2">{r.role}</td>
                      <td className="py-2 text-right">
                        <button
                          className="rounded-lg border border-zinc-200 px-3 py-1 text-xs font-semibold hover:bg-zinc-50 disabled:opacity-50"
                          disabled={disabled}
                          title={title}
                          onClick={() =>
                            setConfirm({
                              kind: "group",
                              label: `${r.group_name} → ${r.role}`,
                              onYes: async () => {
                                await deleteGroupRoleMapping(tenantUuid, r.group_name, r.role, supportHeaders);
                                await refreshAll();
                              },
                            })
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredGroups.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-4 text-xs text-zinc-500">
                      No group→role mappings.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Group->Tenant (read-only here) */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold">Groups mapped to this tenant</div>
            <div className="text-xs text-zinc-500">{groupsMappedToThisTenant.length} groups</div>
          </div>

          <div className="mt-3 text-sm text-zinc-700">
            {groupsMappedToThisTenant.length === 0 ? (
              <div className="text-xs text-zinc-500">No groups currently map to this tenant.</div>
            ) : (
              <ul className="list-disc pl-5">
                {groupsMappedToThisTenant.map((r) => (
                  <li key={r.group_name} className="font-mono">
                    {r.group_name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isPlatformAdmin && (
            <div className="mt-3 text-xs">
              <Link to="/admin/oidc/tenants-oidc" className="text-blue-700 hover:underline">
                Manage tenant & group mappings (platform-admin)
              </Link>
            </div>
          )}
        </div>

        {/* Token validation */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="font-bold">Validate JWT → Derived tenant & roles</div>
          <div className="text-xs text-zinc-500 mt-1">
            Tokens are sensitive. This tool does not store tokens.
          </div>

          <textarea
            className="mt-3 w-full rounded-xl border border-zinc-200 p-3 font-mono text-xs"
            rows={6}
            placeholder="Paste JWT here…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />

          <div className="mt-3 flex gap-2">
            <button
              className="h-10 rounded-xl bg-black px-4 text-sm font-bold text-white disabled:opacity-50"
              disabled={busy || token.trim().length < 50}
              onClick={async () => {
                setBusy(true);
                setErr(null);
                setTokenResult(null);
                try {
                  const r = await validateToken(token.trim(), supportHeaders);
                  setTokenResult(r);
                } catch (e: any) {
                  setErr(errMsg(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Validate
            </button>
            <button
              className="h-10 rounded-xl border border-zinc-200 px-4 text-sm font-semibold hover:bg-zinc-50"
              onClick={() => {
                setToken("");
                setTokenResult(null);
              }}
            >
              Clear
            </button>
          </div>

          {tokenResult && (
            <div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-xs">
              <div className="font-semibold">Derived</div>
              <div className="mt-1">
                Tenant: <span className="font-mono">{tokenResult.derived_tenant_id}</span>
              </div>
              <div>
                Roles:{" "}
                <span className="font-mono">
                  {(tokenResult.derived_roles || []).join(", ") || "(none)"}
                </span>
              </div>
              <div>
                Source: <span className="font-mono">{tokenResult.source}</span>
              </div>
              <div className="mt-3 font-semibold">Claims preview</div>
              <pre className="mt-1 whitespace-pre-wrap">
                {JSON.stringify(tokenResult.claims_preview, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Audit preview */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold">Audit</div>
            <div className="text-xs text-zinc-500">{audit?.configured ? "Configured" : "Not configured"}</div>
          </div>

          {!audit?.configured ? (
            <div className="mt-3 text-xs text-zinc-500">
              Audit log not configured (or table missing). Apply the audit migration and retry.
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[11px] text-zinc-500">
                    <th className="py-2">ts</th>
                    <th className="py-2">action</th>
                    <th className="py-2">target</th>
                    <th className="py-2">actor</th>
                    <th className="py-2">reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit.items || []).map((r, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="py-2 font-mono">{String(r.ts || "")}</td>
                      <td className="py-2 font-mono">{r.action}</td>
                      <td className="py-2 font-mono">
                        {r.target_type}:{r.target_value}
                      </td>
                      <td className="py-2 font-mono">{r.actor_email || r.actor_user || ""}</td>
                      <td className="py-2">{r.reason || ""}</td>
                    </tr>
                  ))}
                  {(audit.items || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 text-xs text-zinc-500">
                        No audit events yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      <DangerConfirmModal
        open={!!confirm}
        title="Confirm removal"
        description={confirm ? `This will remove: ${confirm.label}. Type REMOVE to confirm.` : ""}
        confirmKeyword="REMOVE"
        confirmLabel="Remove"
        requireReason={false}
        onCancel={() => setConfirm(null)}
        onConfirm={async (_args: { reason?: string }) => {
          if (!confirm) return;
          setBusy(true);
          setErr(null);
          try {
            await confirm.onYes();
            setConfirm(null);
          } catch (e: any) {
            setErr(errMsg(e));
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}